import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { confirmPasswordReset, signIn, requestPasswordReset } from "../lib/auth";
import { Logo } from "../components/Logo";
import { AigenticoBadge } from "../components/AigenticoBadge";

export function ForgotPasswordConfirm() {
  const nav = useNavigate();
  const location = useLocation() as { state?: { email?: string } };
  const [email, setEmail] = useState(location.state?.email ?? "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await confirmPasswordReset(email, code, newPassword);
      // Auto sign in with the new password.
      const r = await signIn(email, newPassword);
      if (r.ok) {
        nav("/app", { replace: true });
      } else {
        nav("/signin", { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setError("");
    try {
      await requestPasswordReset(email);
      setError("A new code was sent.");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 py-6">
        <div className="flex items-center justify-between gap-4">
          <Link to="/"><Logo /></Link>
          <a href="https://aigentico.ai" target="_blank" rel="noopener" title="An Aigentico product — visit Aigentico" className="shrink-0">
            <AigenticoBadge size={32} />
          </a>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-6">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6">
          <div>
            <h2 className="mb-2">Choose a new password</h2>
            <p className="text-ink-soft text-sm">
              Enter the 6-digit code we sent to your email, then set a new password.
            </p>
          </div>

          <div>
            <label className="label" htmlFor="email">Email</label>
            <input
              id="email" type="email" autoComplete="email" required
              className="input" value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label className="label" htmlFor="code">Verification code</label>
            <input
              id="code" required autoComplete="one-time-code" inputMode="numeric"
              className="input font-mono tracking-widest text-lg"
              value={code} onChange={e => setCode(e.target.value)} maxLength={6}
            />
          </div>

          <div>
            <label className="label" htmlFor="newPassword">New password</label>
            <input
              id="newPassword" type="password" autoComplete="new-password" required
              className="input" value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
            />
            <p className="text-xs text-ink-faint mt-2">
              Minimum 12 characters. Must contain upper and lower case letters and a digit.
            </p>
          </div>

          {error && (
            <div className="text-aigentico-red text-sm border border-aigentico-red bg-white px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          <button type="submit" disabled={busy} className="btn-primary w-full">
            {busy ? "Saving…" : "Set new password"}
          </button>

          <button type="button" onClick={onResend} className="btn-ghost w-full">
            Resend code
          </button>

          <div className="text-center text-sm">
            <Link to="/signin" className="text-aigentico-navy hover:underline">Back to sign in</Link>
          </div>
        </form>
      </main>
    </div>
  );
}
